/**
 * SaleParticularsModal — live on-chain sale details for a listed token.
 *
 * Opened by the Buy button in NftDetailModal (which closes the detail modal
 * first). All figures are read from the blockchain through the wagmi hook
 * `useAuctionManager` — nothing here is taken from the backend's cached
 * `auctionID`, so the price shown is the current on-chain price.
 *
 * Handles the three sale shapes the AuctionManager supports:
 *   - ERC721  straight sale  → getSaleListingInfo(listingId)
 *   - ERC721  English auction → getAuctionStatus + getActiveAuction + getHighBid
 *   - ERC721  Dutch auction   → getAuctionStatus + getCurrentDutchPrice
 *   - ERC1155 straight sale / auction via the getERC1155* equivalents
 *
 * The auction type is not stored anywhere the UI can read directly, so it is
 * inferred: a running auction with a current price that still moves is Dutch.
 * `getAuctionMeta` reports `auctionType` (1 = English, 2 = Dutch) and is
 * preferred when the diamond implements it.
 */

import React from 'react';
import Modal2 from '../Modal';
import { useAuctionManager } from '../../hooks/useAuctionManager';
import { useAccount, useSwitchChain } from 'wagmi';
import { parseUnits } from 'viem';
import { chainData } from '../../chains';
import { closeListing } from '../../lib/api';

const ZERO = '0x0000000000000000000000000000000000000000';

type SaleKind = 'loading' | 'sale' | 'auction' | 'unavailable';

type SaleData = {
    kind: SaleKind;
    /** 'erc721' | 'erc1155' | 'unknown' — from EIP-165 via the collection. */
    tokenType: string;
    /** 'straight' | 'english' | 'dutch' | '' */
    saleType: string;
    price?: bigint;
    seller?: string;
    quantity?: bigint;
    listedAt?: bigint;
    currentBid?: bigint;
    /** Contract-computed minimum acceptable next bid (English auctions). */
    bidAmount?: bigint;
    reserve?: bigint;
    finishTime?: bigint;
    bidder?: string;
    bidCount?: bigint;
    auctionId?: bigint;
    tokenTypeEnum?: number;
    /** Hash of a completed purchase/bid transaction, for the receipt link. */
    lastTx?: `0x${string}` | null;
    err?: string | null;
};

const EMPTY: SaleData = { kind: 'unavailable', tokenType: 'unknown', saleType: '' };

/** Format a wei-ish native amount with 18 decimals (the chain's native token). */
function fmtNative(v?: bigint | null, decimals = 18): string {
    if (v === undefined || v === null) return '—';
    try {
        const neg = v < 0n;
        const abs = neg ? -v : v;
        const base = 10n ** BigInt(decimals);
        const whole = abs / base;
        const frac = abs % base;
        const fracStr = frac.toString().padStart(decimals, '0').replace(/0+$/, '');
        return `${neg ? '-' : ''}${whole}${fracStr ? '.' + fracStr : ''}`;
    } catch {
        return String(v);
    }
}

function shortAddr(a?: string | null): string {
    if (!a || a === ZERO) return '—';
    return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

function secsLeft(finishTime?: bigint | null, nowSecs?: bigint | null): string {
    if (finishTime === undefined || finishTime === null || finishTime === 0n) return '—';
    // Prefer chain time; fall back to the browser clock only if the block read
    // failed, so a skewed local clock never misreports a live countdown.
    const now = nowSecs ?? BigInt(Math.floor(Date.now() / 1000));
    const diff = finishTime - now;
    if (diff <= 0n) return 'Ended';
    const d = diff / 86400n;
    const h = (diff % 86400n) / 3600n;
    const m = (diff % 3600n) / 60n;
    const s = diff % 60n;
    if (d > 0n) return `${d}d ${h}h`;
    if (h > 0n) return `${h}h ${m}m`;
    if (m > 0n) return `${m}m ${s}s`;
    return `${s}s`;
}

function Row({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px',
                padding: '7px 0',
                borderBottom: '1px solid rgba(255,255,255,0.04)',
            }}
        >
            <span className="text-meta-border" style={{ flexShrink: 0 }}>{label}</span>
            <span
                className="text-meta-border"
                style={{ textAlign: 'right', wordBreak: 'break-all', ...(mono ? { fontFamily: 'monospace', fontSize: '0.82rem' } : {}) }}
            >
                {value}
            </span>
        </div>
    );
}

function TypeBadge({ label, value }: { label: string; value: string }) {
    if (!value || value === '—') return null;
    return (
        <span
            style={{
                display: 'inline-block',
                padding: '2px 8px',
                marginRight: 6,
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: '#9fd2ff',
                background: 'rgba(20,84,141,0.28)',
                border: '1px solid #2f7fd4',
                borderRadius: 999,
            }}
        >
            {label}: {value}
        </span>
    );
}

export type SaleParticularsTarget = {
    /** AuctionManager listing id (straight sale) or auction id. */
    auctionId: number | bigint | string;
    /** The NFT contract holding the token. */
    contractAddress: string;
    /** chainData index for the collection's network. */
    chainIndex?: number;
    /** EVM chain id of the collection — drives the hook's read chain. */
    chainId?: number;
    /** ERC-721 token id, when known. */
    tokenId?: string | number;
    /** ERC-1155 class id, when the collection is 1155. */
    classId?: string | number | bigint;
};

const SaleParticularsModal = ({
    show,
    onClose,
    onListingChanged,
    title,
    target,
}: {
    show?: boolean;
    onClose?: () => void;
    /**
     * Fired AFTER an on-chain listing-close transaction has mined — i.e. after
     * cancelSaleListing, removeListingBeforeEndTime or finalizeEarly succeeded.
     *
     * The collection page uses this to clear that token's "For Sale" badge in
     * place instead of refetching the page. It is deliberately NOT fired on
     * modal dismiss or on a failed tx: the badge should only change once the
     * chain has actually settled it.
     */
    onListingChanged?: () => void;
    title?: any;
    target?: SaleParticularsTarget | null;
}) => {
    // Resolve the chainData row for the TOKEN being displayed. `chainIndex` may
    // arrive as an array index OR as the chainData `network` name (that field
    // holds names like "Flare"/"Anvil", not an index), so accept both and fall
    // back to matching on the raw chain id.
    const chainRow = React.useMemo(() => {
        const rows: any[] = (chainData as any[]) || [];
        const ci = target?.chainIndex;
        if (ci !== undefined && ci !== null) {
            if (typeof ci === 'number' && rows[ci]) return rows[ci];
            // name form: match the `network` field case-insensitively
            const byName = rows.find((r) => r && String(r.network).toLowerCase() === String(ci).toLowerCase());
            if (byName) return byName;
            const byName2 = rows.find((r) => r && String(r.chainName).toLowerCase() === String(ci).toLowerCase());
            if (byName2) return byName2;
            // numeric string that is a valid chain id
            const asNum = Number(ci);
            if (Number.isFinite(asNum)) {
                const byId = rows.find((r) => r && parseInt(String(r.chainId), 10) === asNum);
                if (byId) return byId;
            }
        }
        if (target?.chainId !== undefined) {
            const byId = rows.find((r) => r && parseInt(String(r.chainId), 10) === Number(target.chainId));
            if (byId) return byId;
        }
        return undefined;
    }, [target?.chainIndex, target?.chainId]);

    // Read the chain the TOKEN lives on, not whatever the wallet is connected
    // to. An explicit target.chainId wins; otherwise derive it from the token's
    // own chainData row.
    const readChainId = target?.chainId ?? (chainRow ? parseInt(String(chainRow.chainId), 10) : undefined);
    const am = useAuctionManager(undefined, { chainId: readChainId });

    const [data, setData] = React.useState<SaleData>({ ...EMPTY, kind: 'loading' });
    const [busy, setBusy] = React.useState(false);
    // English-auction bid entry. Empty string means "use the contract's
    // minNextBidAmount"; anything else is the buyer's chosen bid.
    const [bidInput, setBidInput] = React.useState<string>('');

    // Parse a decimal token amount into wei. Returns null on bad input.
    const parseBid = (raw: string): bigint | null => {
        const s = (raw || '').trim();
        if (!s) return null;
        if (!/^\d*\.?\d*$/.test(s) || s === '.' || s === '') return null;
        try {
            return parseUnits(s, 18);
        } catch {
            return null;
        }
    };

    const contractAddress = target?.contractAddress;
    const auctionIdRaw = target?.auctionId;
    const tokenSymbol = chainRow?.tokName || 'SGB';

    // [feePct, model] from the contract; model 0 = SELLER (buyer pays nothing).
    const [feeCfg, setFeeCfg] = React.useState<{ pct: number; model: number } | null>(null);

    // Latest block timestamp on the READ chain, in seconds. Auction countdowns
    // and "listed at" displays must use chain time, not the browser clock, or
    // a skewed local clock shows the wrong time remaining.
    const [chainNow, setChainNow] = React.useState<bigint | null>(null);

    React.useEffect(() => {
        if (!readChainId) return;
        let dead = false;
        (async () => {
            try {
                const block = await am.getChainTime();
                if (dead || block === undefined || block === null) return;
                setChainNow(BigInt(block as bigint));
            } catch {
                // fall back to local time if the block read fails
            }
        })();
        return () => { dead = true; };
    }, [readChainId, am.getBlock]);

    // Mirrors LibDiamond.buyerFee. The contract declares
    // `enum FeeModel { SPLIT, SELLER }`, so SPLIT = 0 and SELLER = 1:
    //   SELLER -> buyer pays nothing (0)
    //   SPLIT  -> buyer pays amount * pct / 200
    // (The earlier version treated model 0 as SELLER, which zeroed the fee on
    // every SPLIT-configured collection.)
    const FEE_MODEL_SPLIT = 0;
    const buyerFeeFor = (amount: bigint): bigint => {
        if (!feeCfg || feeCfg.model !== FEE_MODEL_SPLIT) return 0n; // SELLER -> 0
        return (amount * BigInt(feeCfg.pct)) / 200n;
    };

    const readFeeCfg = async (nft: string, isDead: () => boolean) => {
        try {
            const cfg: any = await am.getEffectiveFeeConfig(nft);
            if (isDead()) return;
            setFeeCfg({
                pct: Number(Array.isArray(cfg) ? cfg[0] : cfg?.feePct ?? 0),
                model: Number(Array.isArray(cfg) ? cfg[1] : cfg?.model ?? 0),
            });
        } catch {
            // no fee config readable: assume SELLER (buyer pays nothing)
            if (isDead()) return;
            setFeeCfg({ pct: 0, model: 0 });
        }
    };

    // The on-chain read, factored out of the mount effect so it can be RE-RUN on
    // its own (after a bid, and on the 30s poll below) without repeating the
    // mount reset.
    //
    // That separation is the whole point. The mount path must clear bidInput,
    // feeCfg, confirmCancel and the action menus — a fresh token is a fresh
    // form. A refresh must do NONE of that: wiping the buyer's half-typed bid
    // every 30 seconds would be actively hostile. So this function only reads
    // and writes `data` (plus feeCfg, which is derived from what it read).
    //
    // `isDead` is the caller's cancellation token — an interval tick and a modal
    // close can race, and a late setData would resurrect a stale view.
    const readOnChain = async (aid: bigint, tokId: bigint | undefined, isDead: () => boolean) => {
        try {
            // 1) A sale listing is keyed by the same id space and is the
            //    common case — getSaleListingInfo returns isListed=false
            //    rather than reverting when the id is a real auction.
            //      -> (bool isListed, uint256 price, address seller,
            //         uint256 timestamp, uint256 quantity,
            //         address nftContract, uint256 tokenId)
            try {
                const sale: any = await am.getSaleListingInfo(aid);
                if (isDead()) return;
                const isListed = Array.isArray(sale) ? sale[0] === true : sale?.isListed === true;
                if (isListed) {
                    setData({
                        kind: 'sale',
                        tokenType: 'erc721',
                        saleType: 'straight',
                        price: BigInt(sale.price ?? sale[1] ?? 0),
                        seller: sale.seller ?? sale[2],
                        quantity: BigInt(sale.quantity ?? sale[4] ?? 1),
                        listedAt: BigInt(sale.timestamp ?? sale[3] ?? 0),
                        auctionId: aid,
                    });
                    // Fee config is keyed by the NFT contract, which the
                    // listing reports — prefer it over the passed address.
                    await readFeeCfg((sale.nftContract ?? sale[5] ?? contractAddress) as string, isDead);
                    return;
                }
            } catch {
                // Not a listing id (or the facet rejected it) — fall
                // through and try the auction path.
            }

            // 2) Otherwise it should be a running auction. getActiveAuction
            //    is keyed by auction id and reverts with !notFound when the
            //    id is not an auction.
            //      -> (address nftContract, uint256 tokenId, address owner,
            //         uint256 currentBid, uint256 reserve, uint256 finishTime,
            //         address bidder, uint256 bidCount, uint8 auctionType)
            let act: any;
            try {
                act = await am.getActiveAuction(aid);
            } catch {
                if (isDead()) return;
                setData({
                    ...EMPTY,
                    tokenType: tokId !== undefined ? 'erc721' : 'unknown',
                    err: 'No active sale or auction found for this token.',
                });
                return;
            }
            if (isDead()) return;

            // 3) Cross-check against the token-keyed status when we know the
            //    token id. Signature is getAuctionStatus(address,uint256).
            let isRunning = true;
            if (tokId !== undefined) {
                try {
                    const status: any = await am.getAuctionStatus(contractAddress, tokId);
                    if (isDead()) return;
                    isRunning = Array.isArray(status) ? status[0] === true : status?.isRunning === true;
                } catch {
                    // treat an unreadable status as running; the active
                    // auction read above already succeeded
                }
            }

            if (!isRunning) {
                setData({
                    ...EMPTY,
                    tokenType: tokId !== undefined ? 'erc721' : 'unknown',
                    err: 'No active sale or auction found for this token.',
                });
                return;
            }

            const g = (src: any, i: number, name: string) =>
                (Array.isArray(src) ? src[i] : src?.[name]) ?? 0n;

            const auctionTypeEnum = Number(g(act, 8, 'auctionType') ?? 0);
            // LibDiamond: `enum AuctionType { ENGLISH, DUTCH }` -> 0 = English,
            // 1 = Dutch. (The old check compared against 2 and mislabelled
            // every auction as English.)
            const saleType = auctionTypeEnum === 1 ? 'dutch' : 'english';

            let price = g(act, 3, 'currentBid');
            if (saleType === 'dutch') {
                try {
                    const dutch: any = await am.getCurrentDutchPrice(aid);
                    if (isDead()) return;
                    const dprice = Array.isArray(dutch) ? dutch[0] : dutch?.currentPrice ?? dutch;
                    if (dprice !== undefined) price = BigInt(dprice);
                } catch {
                    // fall back to the seeded currentBid
                }
            }

            // For an English auction the actionable amount is the contract's
            // minNextBidAmount, not the current bid.
            let bidAmount: bigint | undefined;
            if (saleType === 'english' && tokId !== undefined) {
                try {
                    const min: any = await am.minNextBidAmount(contractAddress, tokId);
                    if (isDead()) return;
                    if (min !== undefined && min !== null) bidAmount = BigInt(Array.isArray(min) ? min[0] : min);
                } catch {
                    // fall back to the current bid
                }
            }

            setData({
                kind: 'auction',
                tokenType: 'erc721',
                saleType,
                price: BigInt(price ?? 0n),
                seller: g(act, 2, 'owner'),
                currentBid: BigInt(g(act, 3, 'currentBid') ?? 0n),
                bidAmount: bidAmount && bidAmount > 0n ? bidAmount : undefined,
                reserve: BigInt(g(act, 4, 'reserve') ?? 0n),
                finishTime: BigInt(g(act, 5, 'finishTime') ?? 0n),
                bidder: g(act, 6, 'bidder'),
                bidCount: BigInt(g(act, 7, 'bidCount') ?? 0n),
                auctionId: aid,
                tokenTypeEnum: auctionTypeEnum,
            });
            // getActiveAuction reports the token's own contract; use it for
            // the fee lookup so an escrowed 721 is charged correctly.
            await readFeeCfg((g(act, 0, 'nftContract') || contractAddress) as string, isDead);
        } catch (e: any) {
            if (isDead()) return;
            setData({
                ...EMPTY,
                err: am.error || e?.shortMessage || e?.message || 'Failed to read sale data from the blockchain.',
            });
        }
    };

    // Reset whenever the modal opens on a different token.
    React.useEffect(() => {
        if (!show || !contractAddress || auctionIdRaw === undefined || auctionIdRaw === null) {
            setData({ ...EMPTY });
            return;
        }
        let dead = false;
        setData((d) => ({ ...d, kind: 'loading', err: null }));
        setBusy(true);
        setBidInput('');
        setFeeCfg(null);
        setConfirmCancel(false);
        setAuctionAction(null);
        setAuctionQuote(null);
        setFinalizeQuote(null);

        const aid = BigInt(auctionIdRaw);
        const tokId = target?.tokenId !== undefined ? BigInt(target.tokenId) : undefined;

        (async () => {
            await readOnChain(aid, tokId, () => dead);
            if (!dead) setBusy(false);
        })();

        return () => {
            dead = true;
        };
        // Re-read whenever the target identity or the target chain changes.
    }, [show, contractAddress, String(auctionIdRaw), String(target?.tokenId), readChainId]);

    // Re-read the current on-chain state without disturbing the form. Safe to call
    // from the post-bid handler and from the 30s interval below.
    //
    // Guarded against overlap: a tick that lands while the previous read is
    // still in flight is skipped rather than queued. Two concurrent reads would
    // race to setData and the slower one could win with older data, and the
    // Dutch price changes every block, so piling up reads is actively harmful.
    const refreshing = React.useRef(false);
    const refresh = React.useCallback(async () => {
        if (refreshing.current) return;
        if (!show || !contractAddress || auctionIdRaw === undefined || auctionIdRaw === null) return;
        const tokId = target?.tokenId !== undefined ? BigInt(target.tokenId) : undefined;
        refreshing.current = true;
        try {
            await readOnChain(BigInt(auctionIdRaw), tokId, () => false);
        } finally {
            refreshing.current = false;
        }
    }, [show, contractAddress, String(auctionIdRaw), String(target?.tokenId)]);

    // Poll while the modal is open on a LIVE auction.
    //
    // Only English and Dutch. A fixed-price sale cannot change under the buyer —
    // the price is set at listing and the listing is removed the moment anyone
    // buys it — so polling it would be pure noise. English changes because
    // anyone can outbid, and Dutch changes because the price falls every block,
    // so both need live values: an English buyer who typed a bid needs to know
    // they've been outbid, and a Dutch buyer's price is stale on arrival.
    //
    // 30s is a deliberate compromise: enough to keep the view honest, sparse
    // enough not to hammer the RPC of every configured chain. This is a
    // read-only view refresh — it is NOT the backend's 30s reconciliation
    // poller, and it never writes.
    React.useEffect(() => {
        if (!show || !contractAddress || auctionIdRaw === undefined || auctionIdRaw === null) return;
        // Gate on what we currently know the listing to be. Before the first
        // read completes `data.kind` is 'loading', so the poll simply waits for
        // the mount effect to classify it and the next tick picks it up.
        const isAuctionLive = data.kind === 'auction';
        if (!isAuctionLive) return;
        const id = window.setInterval(() => {
            refresh();
        }, 30000);
        return () => window.clearInterval(id);
    }, [show, contractAddress, String(auctionIdRaw), data.kind, refresh]);

    const priceLabel = data.saleType === 'dutch' ? 'Current price' : data.saleType === 'english' ? 'Current bid' : 'Price';

    // -----------------------------------------------------------------------
    // Purchase action
    //
    // No approval is required from the BUYER. The seller approves the diamond
    // when listing (createSaleListing), and the NFT either stays with the
    // seller and is pulled on accept, or is escrowed in the diamond for
    // auctions. The buyer's only obligation is to send enough value:
    //   acceptSaleListing / acceptDutchAuctionPrice  -> msg.value >= price + buyerFee
    //   submitNewBid (incremental)                   -> msg.value == bid + buyerFee
    // Overpay is refunded by the contract, except on the incremental-bid path
    // where the value must match exactly.
    // -----------------------------------------------------------------------
    const { address: account, isConnected, chainId: walletChainId } = useAccount();
    const { switchChain, isPending: isSwitching } = useSwitchChain();

    const isDutch = data.kind === 'auction' && data.saleType === 'dutch';
    const isEnglish = data.kind === 'auction' && data.saleType === 'english';
    const isStraight = data.kind === 'sale';
    const wrongChain = Boolean(readChainId && walletChainId && readChainId !== walletChainId);
    const onSwitchChain = () => {
        if (readChainId) switchChain({ chainId: readChainId as any });
    };

    // A buyer cannot be the seller. This is a UI guard only — the contract's
    // own `!cannotBuyOwn` revert remains the authority — but it stops the
    // button inviting the owner to buy their own token, and it backs the
    // read-only "Manage" view an owner gets on a listed item.
    const isSeller = Boolean(
      account &&
        data.seller &&
        String(data.seller) !== ZERO &&
        account.toLowerCase() === String(data.seller).toLowerCase()
    );
    const hasPrice = data.price > 0n;
    // A straight sale sells at one fixed price; an English bid is whatever the
    // buyer typed, defaulting to the contract's minNextBidAmount.
    const minBid = data.bidAmount ?? 0n;
    const typedBid = parseBid(bidInput);
    const bidInvalid = isEnglish && bidInput.trim() !== '' && typedBid === null;
    // What the buyer will actually send as the item amount.
    const itemAmount = isEnglish ? (typedBid ?? minBid) : (data.price ?? 0n);
    const bidTooLow = isEnglish && typedBid !== null && minBid > 0n && typedBid < minBid;
    // The sale is only actionable if we have a real amount. A typed-but-low
    // bid keeps the button live so the contract can reject it with !bidTooLow.
    // `isSeller` keeps the action disabled for the owner — the button becomes
    // a plain "You are the seller" label instead.
    const canAct =
        isConnected &&
        !busy &&
        !wrongChain &&
        !bidInvalid &&
        itemAmount > 0n &&
        !isDutch &&
        !isSeller &&
        (isStraight || isEnglish);

    const actLabel = !isConnected
        ? 'Connect wallet'
        : wrongChain
          ? 'Wrong network'
          : isSeller
            ? 'You are the seller'
            : isDutch
              ? 'Dutch auction'
              : isEnglish
                ? 'Place Bid'
                : 'Buy Now';

    const handleAct = async () => {
        if (!canAct || !contractAddress) return;
        const tokId = target?.tokenId !== undefined ? BigInt(target.tokenId) : undefined;
        // Sale listings and auctions are both keyed by (contract, tokenId); the
        // 721 path needs it. Without it we cannot build the call.
        if (tokId === undefined) {
            setData((d) => ({ ...d, err: 'Token id unavailable — cannot build the purchase call.' }));
            return;
        }
        setBusy(true);
        setData((d) => ({ ...d, err: null }));
        // The item amount the buyer is bidding/paying: the typed English bid, or
        // the listing price for a straight sale.
        const unitPrice = itemAmount;
        try {
            // Fee config was read during load; recompute the fee against THIS
            // amount (it scales with the bid) and send the fee-inclusive total.
            const buyerFee = buyerFeeFor(unitPrice);
            const payValue = unitPrice + buyerFee;

            let hash: `0x${string}` | null = null;
            if (isStraight) {
                hash = await am.acceptSaleListing(contractAddress, tokId, payValue);
            } else if (isEnglish) {
                // submitNewBid requires msg.value == bidAmount + buyerFee and
                // records bid = msg.value - buyerFee, so send the fee-inclusive
                // total and the bare bid separately.
                hash = await am.submitNewBid(contractAddress, tokId, unitPrice, payValue);
            }
            if (hash) {
                setData((d) => ({ ...d, lastTx: hash as `0x${string}` }));
                // The bid is on-chain, so the view we loaded before it is now
                // wrong: currentBid, bidCount and minNextBidAmount have all
                // moved, and for a straight sale the listing may be gone
                // entirely. Re-read so the buyer sees their own bid (and the
                // new minimum for the next one) rather than the pre-bid state.
                //
                // Best-effort: a failed refresh must not turn a successful bid
                // into an error, so this is swallowed and the 30s poll picks
                // the state up on the next tick regardless.
                await refresh();
            } else {
                setData((d) => ({ ...d, err: am.error || 'Transaction was not sent.' }));
            }
        } catch (e: any) {
            setData((d) => ({
                ...d,
                err: am.error || e?.shortMessage || e?.message || 'Purchase failed.',
            }));
        } finally {
            setBusy(false);
        }
    };

    // ----------------------------------------------------------------------
    // Cancel listing (seller, fixed-price only)
    //
    // `cancelSaleListing(nftContract, tokenId)` exists only for straight sale
    // listings on the AuctionManagerSales facet. It is deliberately NOT offered
    // for English/Dutch auctions: those hold bidder ETH in escrow and their
    // removal path (`removeListingBeforeEndTime`) is not implemented on the
    // diamond facet, so a cancel button there would only ever revert. Verified
    // live on Anvil — calling it against an auction id fails with `!notListed`
    // because auctions never populate the saleListings mapping.
    //
    // It is free and unconditional (no escrow, no fee), which is why it needs no
    // value argument. The contract still enforces `msg.sender == seller`; the
    // isSeller check here is UX only.
    // ----------------------------------------------------------------------
    const [confirmCancel, setConfirmCancel] = React.useState(false);
    const canCancel =
        isConnected &&
        !busy &&
        !wrongChain &&
        isSeller &&
        isStraight &&
        target?.tokenId !== undefined;

    const handleCancel = async () => {
        if (!canCancel || !contractAddress) return;
        const tokId = BigInt(target!.tokenId!);
        setBusy(true);
        setData((d) => ({ ...d, err: null }));
        try {
            // callWriteWithReceipt (not cancelSaleListing) returns the already
            // mined receipt: cancelSaleListing's wrapper returns only a hash,
            // and re-waiting on a client that may target another chain hangs
            // forever instead of erroring. See useAuctionManager.
            const res = await am.callWriteWithReceipt('cancelSaleListing', [contractAddress, tokId]);
            if (!res) {
                setData((d) => ({ ...d, err: am.error || 'Cancel transaction was not sent.' }));
                setConfirmCancel(false);
                return;
            }

            // Same notification for the fixed-price path: 202 immediately, the
            // verification happens server-side.
            try {
                await closeListing({ txHash: res.hash, chainId: readChainId });
            } catch (e: any) {
                console.warn('[auction] closeItem notify failed after cancel', res.hash, e);
                setData((d) => ({
                    ...d,
                    err:
                        'Cancelled on-chain, but the listing index was not notified (' +
                        (e?.message || 'backend error') +
                        '). It will show as still listed until it is re-indexed.',
                }));
            }

            // Listing is gone on-chain — drop the sale view rather than leave a
            // stale price on screen. Keep an indexing warning if one was set.
            //
            // Notify the parent the chain has settled this. Fired BEFORE the
            // setData reset so it also runs on the path where the backend
            // notify failed: the listing really is closed on-chain either way,
            // and the poller will fix any indexing drift.
            onListingChanged?.();
            setData((d) => (d.err ? d : { ...EMPTY }));
            setConfirmCancel(false);
        } catch (e: any) {
            setData((d) => ({
                ...d,
                err: am.error || e?.shortMessage || e?.message || 'Cancel listing failed.',
            }));
            setConfirmCancel(false);
        } finally {
            setBusy(false);
        }
    };

    // ----------------------------------------------------------------------
    // Auction actions (English + Dutch): remove listing / finalize early.
    //
    // Both live on the AuctionManager English facet and both are
    // seller-only. They differ from cancelSaleListing in three ways that the
    // UI has to respect:
    //
    //   1. removeListingBeforeEndTime is payable and charges a penalty
    //      (removeListingFeePercentage of the winning bid, half of which is
    //      compensation to the bidder) — the seller must SEND that value.
    //   2. finalizeEarly takes NO value; its penalty comes off the seller's
    //      proceeds. Never put a value field on it.
    //   3. Both percentages are per-contract-overridable and have no public
    //      getter, so the UI must never hardcode them. We ask the contract
    //      via getRemovalFee / getEarlyFinalizeFee, which return the exact
    //      figures finalizeEarly and removeListingBeforeEndTime would use.
    //
    // Dutch auctions are included deliberately: getRemovalFee tests
    // bidderAddress (not bidAmount) for bidder presence, because a Dutch
    // seeds bidAmount = startPrice, so bidAmount > 0 does not imply a bid.
    // Verified live on Anvil: removal flipped isRunning 1 -> 0 on an
    // unbidded Dutch listing.
    // ----------------------------------------------------------------------
    const [auctionAction, setAuctionAction] = React.useState<'remove' | 'finalize' | null>(null);
    const [auctionQuote, setAuctionQuote] = React.useState<
        | {
              penalty: bigint;
              bidderCompensation: bigint;
              fee: bigint;
              refundAmount: bigint;
              hasBidder: boolean;
          }
        | null
    >(null);
    const [finalizeQuote, setFinalizeQuote] = React.useState<
        | { penalty: bigint; bidderReceives: bigint; hasBidder: boolean; reserveMet: boolean }
        | null
    >(null);

    // Dutch auctions carry kind 'auction' too, so isDutch (already derived
    // above) covers them — `data.kind === 'dutch'` would never be true.
    const isAuction = isEnglish || isDutch;

    // Is the connected wallet currently the highest bidder?
    //
    // Both sides are compared case-insensitively because the wallet returns a
    // checksummed address while the contract returns the raw lowercase bytes it
    // was handed — a strict === misses roughly half of all matches depending on
    // whether the original was all-lowercase.
    //
    // `bidCount > 0` matters as much as the address: with no bids yet the
    // `bidder` field is just whatever the struct was initialised with, so an
    // address match alone could report a win that never happened.
    const isHighestBidder =
      isAuction &&
      (data.bidCount ?? 0n) > 0n &&
      Boolean(account) &&
      data.bidder !== undefined &&
      String(data.bidder) !== ZERO &&
      String(account).toLowerCase() === String(data.bidder).toLowerCase();

    const canManageAuction =
        isConnected && !busy && !wrongChain && isSeller && isAuction && target?.tokenId !== undefined;

    // Pull the contract's own numbers when the confirm step opens, so the
    // dialog never shows a locally-computed fee.
    React.useEffect(() => {
        if (auctionAction === null || !canManageAuction || !contractAddress) {
            setAuctionQuote(null);
            setFinalizeQuote(null);
            return;
        }
        let live = true;
        const tokId = BigInt(target!.tokenId!);
        const which = auctionAction;
        setAuctionQuote(null);
        setFinalizeQuote(null);
        (async () => {
            try {
                if (which === 'remove') {
                    const q = await am.getRemovalFee(contractAddress, tokId);
                    if (live) setAuctionQuote(q);
                } else {
                    const q = await am.getEarlyFinalizeFee(contractAddress, tokId);
                    if (live) setFinalizeQuote(q);
                }
            } catch {
                if (live) {
                    setData((d) => ({
                        ...d,
                        err: am.error || 'Could not read the fee for this listing.',
                    }));
                    setAuctionAction(null);
                }
            }
        })();
        return () => {
            live = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auctionAction, canManageAuction, contractAddress, target?.tokenId]);

    const handleAuctionAction = async () => {
        if (!canManageAuction || !contractAddress || auctionAction === null) return;
        const tokId = BigInt(target!.tokenId!);
        const which = auctionAction;
        setBusy(true);
        setData((d) => ({ ...d, err: null }));
        try {
            // Removal is payable: the seller sends exactly the penalty the
            // quote reported. finalizeEarly is non-payable — no value.
            const value = which === 'remove' ? auctionQuote?.penalty ?? 0n : 0n;
            const fn = which === 'remove' ? 'removeListingBeforeEndTime' : 'finalizeEarly';
            const res = await am.callWriteWithReceipt(fn, [contractAddress, tokId], value);
            if (!res) {
                setData((d) => ({
                    ...d,
                    err: am.error || 'That transaction was not sent.',
                }));
                setAuctionAction(null);
                return;
            }

            // The listing is closed on-chain; tell the backend so the index
            // follows. This returns 202 almost immediately — the chain
            // verification happens server-side after the response, so there is
            // nothing here for the modal to wait on. chainId rides along purely
            // so the backend asks one RPC instead of sweeping every chain.
            try {
                await closeListing({ txHash: res.hash, chainId: readChainId });
            } catch (e: any) {
                console.warn('[auction] closeItem notify failed', res.hash, e);
                setData((d) => ({
                    ...d,
                    err:
                        'Done on-chain, but the listing index was not notified (' +
                        (e?.message || 'backend error') +
                        '). It will show as still listed until it is re-indexed.',
                }));
            }

            // finalizeEarly sells to the bidder, so the listing is over either
            // way; clear the auction view rather than show a stale state.
            //
            // Removal returns the NFT to the seller, finalizeEarly transfers it
            // to the bidder — the LISTING is closed in both cases, so the grid
            // card loses its "For Sale" badge either way. Only the badge is
            // patched here; ownership in the grid is not shown and comes from
            // the backend on the next load.
            onListingChanged?.();
            setData((d) => (d.err ? d : { ...EMPTY }));
            setAuctionAction(null);
        } catch (e: any) {
            setData((d) => ({
                ...d,
                err: am.error || e?.shortMessage || e?.message || 'That action failed.',
            }));
            setAuctionAction(null);
        } finally {
            setBusy(false);
        }
    };

    const content = (
        <div>
            <div style={{ marginBottom: '12px' }}>
                <TypeBadge label="Token" value={data.tokenType === 'unknown' ? '' : data.tokenType} />
                <TypeBadge
                    label="Sale"
                    value={data.saleType === 'straight' ? 'Straight sale' : data.saleType === 'dutch' ? 'Dutch auction' : data.saleType === 'english' ? 'English auction' : ''}
                />
            </div>

            {data.kind === 'loading' && (
                <div style={{ textAlign: 'center', color: '#aeb6c8', padding: '28px 0' }}>
                    Reading sale data from the blockchain…
                </div>
            )}

            {data.kind === 'unavailable' && data.err && (
                <div style={{ textAlign: 'center', color: '#aeb6c8', padding: '28px 0' }}>{data.err}</div>
            )}

            {(data.kind === 'sale' || data.kind === 'auction') && (
                <div>
                    {/* Non-fatal notice. data.err is also used for hard failures,
                        but those reset kind to 'unavailable' and render above —
                        so anything shown here while a listing is still displayed
                        is the "done on-chain, index lagging" warning, which must
                        stay visible rather than being cleared with the view. */}
                    {data.err && (
                        <div
                            style={{
                                marginBottom: '10px',
                                padding: '9px 11px',
                                fontSize: '0.78rem',
                                color: '#e0b060',
                                background: 'rgba(224,176,96,0.1)',
                                border: '1px solid rgba(224,176,96,0.35)',
                                borderRadius: 4,
                            }}
                        >
                            {data.err}
                        </div>
                    )}
                    <Row label={priceLabel} value={<strong>{fmtNative(data.price)} {tokenSymbol}</strong>} />
                    {data.kind === 'auction' && data.bidCount !== undefined && (
                        <Row label="Bids" value={String(data.bidCount)} />
                    )}
                    {isAuction && data.bidder && data.bidder !== ZERO && (
                        <Row
                            label="Highest bidder"
                            value={
                                isHighestBidder ? (
                                    <span style={{ color: '#20ff43', fontWeight: 700 }}>
                                        You ({shortAddr(data.bidder)})
                                    </span>
                                ) : (
                                    <span>{shortAddr(data.bidder)}</span>
                                )
                            }
                            mono={!isHighestBidder}
                        />
                    )}
                    {/* Explicit status line. The row above only appears once
                        someone has bid, so a first-time viewer gets no signal at
                        all; this says plainly whether the connected wallet is
                        winning, outbid, or simply not involved yet. */}
                    {isAuction && !isSeller && (
                        <div
                            style={{
                                marginTop: 6,
                                padding: '6px 10px',
                                borderRadius: 4,
                                fontSize: 12,
                                fontWeight: 700,
                                border: `1px solid ${
                                    isHighestBidder ? 'rgba(32,255,67,.4)' : 'rgba(224,176,96,.35)'
                                }`,
                                background: isHighestBidder ? 'rgba(32,255,67,.10)' : 'rgba(224,176,96,.08)',
                                color: isHighestBidder ? '#20ff43' : '#e0b060',
                            }}
                        >
                            {isHighestBidder
                                ? 'You are the highest bidder'
                                : (data.bidCount ?? 0n) > 0n
                                  ? 'You have been outbid'
                                  : 'No bids yet — place the first bid'}
                        </div>
                    )}
                    {isEnglish && data.bidAmount !== undefined && (
                        <Row label="Minimum next bid" value={`${fmtNative(data.bidAmount)} ${tokenSymbol}`} />
                    )}
                    {data.kind === 'auction' && data.reserve !== undefined && data.reserve > 0n && (
                        <Row label="Reserve" value={`${fmtNative(data.reserve)} ${tokenSymbol}`} />
                    )}
                    {data.kind === 'auction' && (
                        <Row label="Ends in" value={secsLeft(data.finishTime, chainNow)} />
                    )}
                    <Row label="Seller" value={shortAddr(data.seller)} mono />
                    {data.kind === 'sale' && data.quantity !== undefined && (
                        <Row label="Quantity" value={String(data.quantity)} />
                    )}
                    {data.kind === 'sale' && data.listedAt !== undefined && data.listedAt > 0n && (
                        <Row
                            label="Listed"
                            value={new Date(Number(data.listedAt) * 1000).toLocaleString()}
                        />
                    )}
                    {target?.contractAddress && (
                        <Row label="Contract" value={shortAddr(target.contractAddress)} mono />
                    )}

                    {/* Bid entry for English auctions. Empty = use the minimum.
                        Buyer-only: the seller has no bid to place. */}
                    {isEnglish && !isSeller && (
                        <div style={{ marginTop: '12px' }}>
                            <label
                                htmlFor="sp-bid-input"
                                style={{ display: 'block', fontSize: '0.78rem', color: '#aeb6c8', marginBottom: '5px' }}
                            >
                                Your bid
                                {minBid > 0n && (
                                    <span style={{ opacity: 0.75 }}>
                                        {' '}
                                        (min {fmtNative(minBid)} {tokenSymbol})
                                    </span>
                                )}
                            </label>
                            <input
                                id="sp-bid-input"
                                type="text"
                                inputMode="decimal"
                                autoComplete="off"
                                placeholder={minBid > 0n ? fmtNative(minBid) : '0.0'}
                                value={bidInput}
                                onChange={(e) => setBidInput(e.target.value)}
                                style={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    padding: '9px 11px',
                                    fontFamily: 'monospace',
                                    fontSize: '0.9rem',
                                    color: '#e8ecf5',
                                    background: 'rgba(0,0,0,0.35)',
                                    border: `1px solid ${bidInvalid || bidTooLow ? '#b4453c' : 'rgba(255,255,255,0.14)'}`,
                                    borderRadius: 4,
                                    outline: 'none',
                                }}
                            />
                            {bidInvalid && (
                                <div style={{ marginTop: '5px', fontSize: '0.75rem', color: '#e08a80' }}>
                                    Enter a valid amount, e.g. 1.25
                                </div>
                            )}
                            {!bidInvalid && bidTooLow && (
                                <div style={{ marginTop: '5px', fontSize: '0.75rem', color: '#e0b060' }}>
                                    Below the minimum next bid — the contract will reject it.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Buyer fee + total, recomputed against the entered bid.
                        Buyer-only: "You pay" is meaningless to the seller. */}
                    {(isStraight || isEnglish) && itemAmount > 0n && !isSeller && (
                        <>
                            <div style={{ marginTop: '12px' }}>
                                <Row
                                    label={`Buyer fee${feeCfg && feeCfg.model === FEE_MODEL_SPLIT ? ` (${feeCfg.pct / 2}%)` : ''}`}
                                    value={`${fmtNative(buyerFeeFor(itemAmount))} ${tokenSymbol}`}
                                />
                                <Row
                                    label="You pay"
                                    value={
                                        <strong>
                                            {fmtNative(itemAmount + buyerFeeFor(itemAmount))} {tokenSymbol}
                                        </strong>
                                    }
                                />
                            </div>
                        </>
                    )}
                </div>
            )}

            {data.kind === 'auction' && data.saleType === 'dutch' && (
                <div
                    style={{
                        marginTop: '12px',
                        padding: '10px 12px',
                        fontSize: '0.8rem',
                        color: '#aeb6c8',
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 4,
                    }}
                >
                    Dutch auctions are priced continuously and can&apos;t be bought from this
                    screen yet.
                </div>
            )}

            <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {wrongChain ? (
                    <button className="pixel-btn" style={{ width: '100%' }} onClick={onSwitchChain} disabled={isSwitching}>
     {isSwitching ? 'Switching…' : `Switch to ${chainRow?.network || `chain ${readChainId}`}`}
                    </button>
                ) : (
                    <button
                        className="pixel-btn"
                        style={{ width: '100%', opacity: canAct ? 1 : 0.5 }}
                        disabled={!canAct}
                        onClick={handleAct}
                    >
                        {actLabel}
                    </button>
                )}
                {/* Cancel listing — fixed-price only, and only for the seller.
                    Hidden entirely when it would not apply, so an auction owner
                    is not offered a button the contract cannot honour. */}
                {canCancel && (
                    <>
                        {confirmCancel ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div
                                    style={{
                                        padding: '9px 11px',
                                        fontSize: '0.78rem',
                                        color: '#e0b060',
                                        background: 'rgba(224,176,96,0.1)',
                                        border: '1px solid rgba(224,176,96,0.35)',
                                        borderRadius: 4,
                                    }}
                                >
                                    Remove this listing? The token will stop being for sale and the
                                    price will be cleared. This cannot be undone.
                                </div>
                                <button
                                    className="pixel-btn"
                                    style={{
                                        width: '100%',
                                        background: 'rgba(180,69,60,0.18)',
                                        border: '1px solid #b4453c',
                                        color: '#ffd9d5',
                                    }}
                                    onClick={handleCancel}
                                    disabled={busy}
                                >
                                    {busy ? 'Cancelling…' : 'Yes, remove listing'}
                                </button>
                                <button
                                    className="pixel-btn"
                                    style={{
                                        width: '100%',
                                        background: 'transparent',
                                        border: '1px solid rgba(255,255,255,0.12)',
                                        color: '#aeb6c8',
                                    }}
                                    onClick={() => setConfirmCancel(false)}
                                    disabled={busy}
                                >
                                    Keep listing
                                </button>
                            </div>
                        ) : (
                            <button
                                className="pixel-btn"
                                style={{
                                    width: '100%',
                                    background: 'transparent',
                                    border: '1px solid rgba(180,69,60,0.5)',
                                    color: '#e8a49c',
                                }}
                                onClick={() => setConfirmCancel(true)}
                                disabled={busy}
                            >
                                Cancel listing
                            </button>
                        )}
                    </>
                )}
                {/* Auction actions — English + Dutch, seller only. Shown as a
                    two-step confirm that quotes the contract's own fee
                    figures, so the numbers on screen are the ones the
                    transaction will actually use. */}
                {canManageAuction && (
                    <>
                        {auctionAction === null ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <button
                                    className="pixel-btn"
                                    style={{
                                        width: '100%',
                                        background: 'transparent',
                                        border: '1px solid rgba(180,69,60,0.5)',
                                        color: '#e8a49c',
                                    }}
                                    onClick={() => setAuctionAction('remove')}
                                    disabled={busy}
                                >
                                    Remove listing
                                </button>
                                <button
                                    className="pixel-btn"
                                    style={{
                                        width: '100%',
                                        background: 'transparent',
                                        border: '1px solid rgba(224,176,96,0.5)',
                                        color: '#e0b060',
                                    }}
                                    onClick={() => setAuctionAction('finalize')}
                                    disabled={busy}
                                >
                                    End auction early
                                </button>
                            </div>
                        ) : (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px',
                                    padding: '10px 11px',
                                    border: '1px solid rgba(224,176,96,0.35)',
                                    background: 'rgba(224,176,96,0.07)',
                                    borderRadius: 4,
                                }}
                            >
                                {auctionAction === 'remove' ? (
                                    <>
                                        <div style={{ fontSize: '0.8rem', color: '#e0b060' }}>
                                            Remove this auction listing? The NFT returns to you and any
                                            winning bid is refunded. This cannot be undone.
                                        </div>
                                        {auctionQuote ? (
                                            <>
                                                {auctionQuote.hasBidder ? (
                                                    <>
                                                        <Row
                                                            label="Removal fee"
                                                            value={`${fmtNative(auctionQuote.penalty)} ${tokenSymbol}`}
                                                        />
                                                        <Row
                                                            label="Bidder refund"
                                                            value={`${fmtNative(auctionQuote.refundAmount)} ${tokenSymbol}`}
                                                        />
                                                        <Row
                                                            label="Bidder compensation"
                                                            value={`${fmtNative(auctionQuote.bidderCompensation)} ${tokenSymbol}`}
                                                        />
                                                    </>
                                                ) : (
                                                    <Row label="Removal fee" value="Free — no bids yet" />
                                                )}
                                            </>
                                        ) : (
                                            <div style={{ fontSize: '0.75rem', color: '#aeb6c8' }}>
                                                Reading the removal fee…
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <div style={{ fontSize: '0.8rem', color: '#e0b060' }}>
                                            {finalizeQuote?.hasBidder
                                                ? 'End this auction now and sell to the highest bidder. The penalty comes out of your proceeds.'
                                                : 'End this auction now. With no qualifying bids the NFT returns to you, at no cost.'}
                                        </div>
                                        {finalizeQuote ? (
                                            finalizeQuote.hasBidder ? (
                                                <>
                                                    <Row
                                                        label="Early-finalize fee"
                                                        value={`${fmtNative(finalizeQuote.penalty)} ${tokenSymbol}`}
                                                    />
                                                    <Row
                                                        label="You receive"
                                                        value={`${fmtNative(finalizeQuote.bidderReceives - finalizeQuote.penalty)} ${tokenSymbol}`}
                                                    />
                                                </>
                                            ) : (
                                                <Row label="Early-finalize fee" value="Free — no qualifying bids" />
                                            )
                                        ) : (
                                            <div style={{ fontSize: '0.75rem', color: '#aeb6c8' }}>
                                                Reading the early-finalize fee…
                                            </div>
                                        )}
                                    </>
                                )}
                                <button
                                    className="pixel-btn"
                                    style={{
                                        width: '100%',
                                        background:
                                            auctionAction === 'remove'
                                                ? 'rgba(180,69,60,0.18)'
                                                : 'rgba(224,176,96,0.18)',
                                        border: `1px solid ${auctionAction === 'remove' ? '#b4453c' : '#e0b060'}`,
                                        color: '#ffd9d5',
                                    }}
                                    onClick={handleAuctionAction}
                                    disabled={busy}
                                >
                                    {busy
                                        ? 'Working…'
                                        : auctionAction === 'remove'
                                          ? 'Yes, remove listing'
                                          : 'Yes, end auction early'}
                                </button>
                                <button
                                    className="pixel-btn"
                                    style={{
                                        width: '100%',
                                        background: 'transparent',
                                        border: '1px solid rgba(255,255,255,0.12)',
                                        color: '#aeb6c8',
                                    }}
                                    onClick={() => setAuctionAction(null)}
                                    disabled={busy}
                                >
                                    Keep listing
                                </button>
                            </div>
                        )}
                    </>
                )}
                <button
                    className="pixel-btn"
                    style={{ width: '100%', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: '#aeb6c8' }}
                    onClick={onClose}
                >
                    Close
                </button>
            </div>
        </div>
    );

    return (
        <Modal2
            show={show}
            modalTitle={title || 'Sale Details'}
            content={content}
            handleClose={onClose}
            panelClassName="pixel-modal-sale"
        />
    );
};

export default SaleParticularsModal;
